import React, { ReactNode } from 'react';
import { useBrand } from '../contexts/BrandContext';
import { Button, Card } from 'flowbite-react';
import { HiLockClosed, HiSparkles } from 'react-icons/hi';

interface FeatureGateProps {
  /** Feature name to check against brand.enabled_features */
  feature: string;
  /** Content to render if feature is enabled */
  children: ReactNode;
  /**
   * Gate type:
   * - 'hard': Completely hide content if feature disabled
   * - 'soft': Show upgrade prompt if feature disabled
   */
  gateType?: 'hard' | 'soft';
  /** Custom message for upgrade prompt (soft gate only) */
  upgradeMessage?: string;
  /** Optional callback when upgrade button clicked */
  onUpgradeClick?: () => void;
  /** Custom fallback component for soft gate */
  fallback?: ReactNode;
}

/**
 * FeatureGate Component
 *
 * Conditionally renders content based on whether a feature is enabled
 * for the current brand.
 *
 * Usage:
 * ```tsx
 * // Hard gate - completely hides content
 * <FeatureGate feature="advanced_analytics">
 *   <AnalyticsDashboard />
 * </FeatureGate>
 *
 * // Soft gate - shows upgrade prompt
 * <FeatureGate feature="ai_insights" gateType="soft">
 *   <AIInsightsPanel />
 * </FeatureGate>
 * ```
 */
export function FeatureGate({
  feature,
  children,
  gateType = 'hard',
  upgradeMessage,
  onUpgradeClick,
  fallback,
}: FeatureGateProps) {
  const { isFeatureEnabled, brand, isLoading } = useBrand();

  // While loading brand config, show nothing to avoid flicker
  if (isLoading) {
    return null;
  }

  // Check if feature is enabled
  const enabled = isFeatureEnabled(feature);

  if (enabled) {
    return <>{children}</>;
  }

  // Hard gate: completely hide
  if (gateType === 'hard') {
    return null;
  }

  // Soft gate: show upgrade prompt
  if (fallback) {
    return <>{fallback}</>;
  }

  const defaultMessage = `${feature.replace(/_/g, ' ')} is available on higher tier plans.`;
  const message = upgradeMessage || defaultMessage;

  return (
    <Card className="bg-gray-50 dark:bg-gray-800">
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="mb-4 rounded-full bg-gray-200 p-3 dark:bg-gray-700">
          <HiLockClosed className="h-6 w-6 text-gray-500 dark:text-gray-400" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
          Feature Not Available
        </h3>
        <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
          {message}
        </p>
        <p className="mb-4 text-xs text-gray-500">
          Current plan: <span className="font-medium">{brand?.Pricing_Tier || 'Free'}</span>
        </p>
        <Button
          color="purple"
          onClick={onUpgradeClick || (() => window.location.href = '/settings?tab=billing')}
        >
          <HiSparkles className="mr-2 h-4 w-4" />
          Upgrade Plan
        </Button>
      </div>
    </Card>
  );
}

/**
 * Hook version for programmatic feature checking
 */
export function useFeatureGate(feature: string): {
  isEnabled: boolean;
  isLoading: boolean;
  tier: string | undefined;
} {
  const { isFeatureEnabled, isLoading, brand } = useBrand();

  return {
    isEnabled: isFeatureEnabled(feature),
    isLoading,
    tier: brand?.Pricing_Tier,
  };
}

export default FeatureGate;
